import { Component, computed, effect, inject, input, linkedSignal, untracked } from '@angular/core';
import {
  InputLabel,
  RadioGroup,
  Radio,
  HelpButton,
  Button,
  InputSimple,
  Select,
} from 'excellence-ui';
import { form, FormField, required, validate } from '@angular/forms/signals';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { NgIcon } from '@ng-icons/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  EnergyInterfaceCreateDialogFormModel,
  EnergyInterfacesStore,
  EnergyInterfaceState,
} from '../../stores/energy-interfaces.store';
import { validateMalo } from '../../validators/malo.validator';
import { LocationsStore } from '../../../locations/store/locations.store';
import { LocationNamePipe } from '../../../locations/pipes/location-name-pipe';
import { UserInfoService } from '../../../user/services/user-info.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-energy-interface-create-form',
  imports: [
    InputLabel,
    InputSimple,
    Select,
    RadioGroup,
    FormField,
    Radio,
    FieldErrors,
    HelpButton,
    Button,
    NgIcon,
    TranslocoPipe,
    LocationNamePipe,
  ],
  templateUrl: './energy-interface-create-form.html',
})
export class EnergyInterfaceCreateForm {
  private energyInterfacesStore = inject(EnergyInterfacesStore);
  private translocoService = inject(TranslocoService);
  protected locationsStore = inject(LocationsStore);
  private userInfoService = inject(UserInfoService);

  protected maloMeloMeterArticleUrl = environment.articles.meloMaloMeter;

  locationId = input.required<string | null>();
  utilityType = input<Exclude<
    Exclude<EnergyInterfaceState['data'], null>['utilityType'],
    undefined
  > | null>(null);
  showUtilityTypeInput = input.required<boolean>();
  energyInterfaceData = input<EnergyInterfaceState['data'] | null>(null);
  showFormValidityErrors = input(false);

  model = linkedSignal<EnergyInterfaceCreateDialogFormModel>(() => {
    const data = this.energyInterfaceData();
    const emptyData = EnergyInterfacesStore.getEmptyCreateDialogModel();
    if (data === null) {
      return emptyData;
    }
    return {
      ...data,
      utilityType: data.utilityType ?? emptyData.utilityType,
      locationID: data.locationID ?? emptyData.locationID,
      maloID: data.maloID ?? emptyData.maloID,
      meterID: data.meterID ?? emptyData.meterID,
      measurementType: data.measurementType ?? emptyData.measurementType,
      flowDirection: data.flowDirection ?? emptyData.flowDirection,
    };
  });
  private currentUtilityType = computed(() => this.model().utilityType);

  form = form(this.model, (schemaPath) => {
    // label
    required(schemaPath.label);
    // locationID
    required(schemaPath.locationID);
    // utilityType
    required(schemaPath.utilityType);
    // malo
    validate(schemaPath.maloID, ({ value }) => {
      const maloID = value();
      if (maloID === '') {
        return null; // Malo ID is optional
      }
      try {
        validateMalo(maloID);
        return null;
      } catch {
        return {
          kind: 'malo',
          message: this.translocoService.translate(
            'cockpit.energyInterfaces.createDialog.maloIDInvalid',
          ),
        };
      }
    });
  });

  constructor() {
    effect(() => {
      const locationId = this.locationId();
      const utilityType = this.utilityType();
      this.model.update((model) => ({
        ...model,
        locationID: locationId ?? model.locationID,
        utilityType: utilityType ?? model.utilityType,
      }));
    });

    // Auto-generate label when utility type changes and label is untouched/empty
    effect(async () => {
      const utilityType = this.currentUtilityType();
      const allEntities = this.energyInterfacesStore.energyInterfaces() ?? [];
      if (this.form.label().dirty()) {
        return;
      }
      const prefix = await this.getPrefixForUtilityType(utilityType);
      const prefixRegExp = new RegExp(`^${prefix} \\d+$`);
      const entities = allEntities.filter((ei) => prefixRegExp.test(ei.data?.label ?? ''));
      let newNameIndex = 1;
      while (entities.find((ei) => ei.data?.label === `${prefix} ${newNameIndex}`)) {
        newNameIndex++;
      }
      untracked(() =>
        this.model.update((model) => ({
          ...model,
          label: utilityType ? `${prefix} ${newNameIndex}` : '',
        })),
      );
    });
  }

  markAsTouched() {
    this.form.label().markAsTouched();
    this.form.locationID().markAsTouched();
    this.form.utilityType().markAsTouched();
    this.form.maloID().markAsTouched();
    this.form.meterID().markAsTouched();
    this.form.measurementType().markAsTouched();
    this.form.flowDirection().markAsTouched();
  }

  private async getPrefixForUtilityType(utilityType: string): Promise<string> {
    const lang = this.userInfoService.language();
    await this.userInfoService.applyLanguage(lang); // ensure language is loaded

    if (utilityType === 'GAS') {
      return this.translocoService.translate(
        'cockpit.energyInterfaces.createDialog.emptyLabelPrefixGas',
      );
    } else {
      return this.translocoService.translate(
        'cockpit.energyInterfaces.createDialog.emptyLabelPrefixPower',
      );
    }
  }
}
